import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { get_list_of_setting, get_machine_category_with_groups } from '@/utils/api';
import useAppSetting from '@/hooks/useAppSetting';
import { get_common_sub_categories_by_machine_group } from './mbmr_api';
import {
  buildCascaderLeafValue,
  resolveMachineDetails,
  LockedMachineSetupPanel,
} from './machineRoutingHelpers';
import {
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Tooltip,
  Switch,
  Tag,
  Cascader,
  Checkbox,
} from 'antd';
import { Activity, Clock, Gauge, CheckCircle, Edit3, Trash2, Lock } from 'lucide-react';
import { useTheme } from '@/App';

const { TextArea } = Input;

const GRACE_WINDOW_UNITS = [
  { value: 'sec', label: 'Seconds' },
  { value: 'min', label: 'Minutes' },
  { value: 'hrs', label: 'Hours' },
];

const parseGraceWindow = (graceWindow, value, unit) => {
  if (value !== undefined && value !== null && unit) {
    return { value: Number(value), unit };
  }
  const match = String(graceWindow || '').match(/(\d+(?:\.\d+)?)\s*(sec|second|min|minute|hr|hour)s?/i);
  if (!match) return { value: 1, unit: 'min' };
  const rawUnit = match[2].toLowerCase();
  return {
    value: Number(match[1]),
    unit: rawUnit.startsWith('sec') ? 'sec' : rawUnit.startsWith('min') ? 'min' : 'hrs',
  };
};

const formatGraceWindow = (value, unit) => `± ${value} ${unit}`;

// Automatic readings are taken by the beat task (every 5 s) and a stale tag is retried until the grace window closes,
// so the grace has to cover two ticks, and it can be at most half the interval. Mirrors services/Monitoring/monitoring_rules.py.
const MIN_AUTO_GRACE_SECONDS = 10;
const GRACE_UNIT_SECONDS = { sec: 1, min: 60, hrs: 3600 };
const autoGraceProblem = (vals) => {
  const frequency = Number(vals.monitoring_frequency) || 0;
  if (vals.mode !== 'Automatic' || frequency <= 0) return null;
  const grace = Math.floor((Number(vals.grace_window_value) || 0) * (GRACE_UNIT_SECONDS[vals.grace_window_unit] || 0));
  if (grace < MIN_AUTO_GRACE_SECONDS) {
    return `Automatic needs a grace window of at least ${MIN_AUTO_GRACE_SECONDS} seconds so a stale reading can be retried.`;
  }
  if (frequency < 2 * grace + 1) {
    return `With a ${grace} second grace window the frequency must be at least ${2 * grace + 1} seconds (grace is at most half the interval).`;
  }
  return null;
};
// monitoring_frequency is stored in seconds; show it in the largest whole unit (600 -> "10 min").
const formatFrequency = (seconds) => {
  const sec = Number(seconds);
  if (!sec) return '';
  if (sec % 3600 === 0) return `${sec / 3600} hr`;
  if (sec % 60 === 0) return `${sec / 60} min`;
  return `${sec} sec`;
};

// REMOVED: MachineRegisterSection component
const isBlank = (v) => v === null || v === undefined || v === '';
// No lower/upper limit means record-only: there is nothing to deviate from.
const hasNoLimits = (vals) => isBlank(vals.min_value) && isBlank(vals.max_value);

// Edit mode: saved items may carry no unit, so fill it from the machine group's
// parameter list (which resolves the unit from the process control) once loaded.
const UnitSync = ({ values, setFieldValue, machineParameters }) => {
  useEffect(() => {
    if (!values.parameter_id || values.parameter_unit) return;
    const match = (machineParameters || []).find(
      (p) => String(p.id) === String(values.parameter_id)
    );
    if (match?.unit) setFieldValue('parameter_unit', match.unit);
  }, [values.parameter_id, values.parameter_unit, machineParameters, setFieldValue]);
  return null;
};

const validationSchema = Yup.object().shape({
  machine_type_id: Yup.mixed().nullable().required('Machine is required'),
  machine_group_id: Yup.mixed().nullable().required('Machine group is required'),
  machine_id: Yup.string().nullable(),
  parameter_id: Yup.string().nullable().required('Parameter is required'),
    reading_count: Yup.number()
    .nullable()
    .when(['startTime', 'endTime', 'mode', 'monitoring_frequency'], {
      is: (startTime, endTime, mode, monitoringFrequency) =>
        !startTime && !endTime && mode !== 'auto' && !monitoringFrequency,
      then: (schema) => schema.required('Number of readings required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  monitoring_frequency: Yup.number().nullable(),
  grace_window_value: Yup.number()
    .nullable()
    .min(0, 'Grace Window cannot be negative')
    .required('Grace Window is required'),
  grace_window_unit: Yup.string().nullable().required('Grace Window unit is required'),
  exp_value: Yup.string()
    .nullable()
    .when('parameter_data_type', {
      is: (dataType) => dataType === 'Boolean' || dataType === 'String',
      then: (schema) => schema.required('Expected value is required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  min_value: Yup.number().nullable(),
  max_value: Yup.number()
    .nullable()
    .when('min_value', {
      is: (val) => val !== null,
      then: (schema) => schema.min(Yup.ref('min_value'), 'Max must be >= Min'),
    }),
  instruction: Yup.string().nullable(),
  startTime: Yup.boolean().nullable(),
  endTime: Yup.boolean().nullable(),
  mode: Yup.string().nullable().required('Choose Auto/Manual'),
}).test('auto-grace', function checkAutoGrace(vals) {
  const message = autoGraceProblem(vals || {});
  return message ? this.createError({ path: 'grace_window_value', message }) : true;
});

// Deviation rule options — when a reading is treated as a deviation.
const DEVIATION_RULE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'less_than_min', label: 'Less than Min' },
  { value: 'greater_than_max', label: 'Greater than Max' },
  { value: 'less_than_min_or_greater_than_max', label: 'Less than Min or Greater than Max' },
];

export default function Monitoring({
  editIndex,
  onSubmit = (values) => console.log('Submitted values:', values),
  operation_duration = 0,
  app_settings: appSettingsProp,
  lockedRuleFields,
  one_machine_rule_enabled = false,
  configured_machines = [],
  machine_setup_entries = [],
  process_id,
  values = {
    machine_type_id: null,
    machine_type_name: null,
    machine_group_id: null,
    machine_id: null,
    machine_name: null,
    parameter_id: null,
    parameter: null,
    parameter_unit: null,
    parameter_data_type: null,
    reading_count: null,
    monitoring_frequency: null,
    min_value: null,
    max_value: null,
    deviation_rule: 'none',
    exp_value: null,
    instruction: null,
    startTime: false,
    endTime: false,
    mode: 'Manual',
    is_qr_scan: false,
    is_kitting: false,
    grace_window: '± 1 min',
    grace_window_value: 1,
    grace_window_unit: 'min',
    trigger_type: 'Time Based',
    escalation_delay: '2 Min',
    // Editable Runtime Execution Rules (defaults from rule). Saved per item (separate JSON).
    execution_rules: null,
  },
}) {
  const rulesEnabled = useAppSetting('Rules');
  const [appSettings, setAppSettings] = useState(appSettingsProp || []);
  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);
  const [frequencyUnit, setFrequencyUnit] = useState('sec'); // 'sec' or 'min'
  const hasKnownDuration = operation_duration > 0;
  // Auto-apply machine from setup if rule is enabled
  const ruleLockedCategory =
    one_machine_rule_enabled && machine_setup_entries?.[0]?.machine_type_id
      ? machine_setup_entries[0].machine_type_id
      : null;
  const ruleLockedGroup =
    one_machine_rule_enabled && machine_setup_entries?.[0]?.machine_group_id
      ? machine_setup_entries[0].machine_group_id
      : null;
  const ruleLockedLabel = machine_setup_entries?.[0]?.machine_type || '';
  const ruleLockedMachineIds = (() => {
    if (Array.isArray(configured_machines) && configured_machines.length) {
      return configured_machines.map(Number).filter(Boolean);
    }
    const setup = machine_setup_entries?.[0] || {};
    if (Array.isArray(setup.machine_ids) && setup.machine_ids.length) {
      return setup.machine_ids.map(Number).filter(Boolean);
    }
    if (setup.machine_ids_csv) {
      return String(setup.machine_ids_csv).split(',').map(Number).filter(Boolean);
    }
    return setup.machine_id ? [Number(setup.machine_id)].filter(Boolean) : [];
  })();
  const ruleLockedMachineId = ruleLockedMachineIds[0] || null;
  const setupMachineDetails = resolveMachineDetails(
    ruleLockedMachineIds,
    machineTypeWithGroups,
    machine_setup_entries?.[0] || {}
  );
  const [cascaderValue, setCascaderValue] = useState(null);
  const hasRuleValue = (key) =>
    lockedRuleFields?.[key] !== undefined &&
    lockedRuleFields?.[key] !== null &&
    lockedRuleFields?.[key] !== '';
  const isRuleFieldLocked = (key) =>
    hasRuleValue(key) && lockedRuleFields?.[`${key}_editable`] !== true;
  const isRuntimeRuleLocked = (key) =>
    hasRuleValue(key) && lockedRuleFields?.[`${key}_editable`] !== true;
  const parsedGraceWindow = parseGraceWindow(
    lockedRuleFields?.grace_window || values.grace_window,
    lockedRuleFields?.grace_window_value ?? values.grace_window_value,
    lockedRuleFields?.grace_window_unit || values.grace_window_unit
  );

  useEffect(() => {
    if (appSettingsProp?.length) {
      setAppSettings(appSettingsProp);
    } else {
      get_list_of_setting((res) => {
        if (res?.data?.data) setAppSettings(res.data.data);
      });
    }
  }, [appSettingsProp]);

  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    }, process_id);
  }, [process_id]);

  // Restore cascader value on edit / when data loads
  useEffect(() => {
    if (!machineTypeWithGroups.length) return;
    const catId = ruleLockedCategory || values.machine_type_id;
    const grpId = ruleLockedGroup || values.machine_group_id;
    const selectedMachineIds = ruleLockedMachineIds.length
      ? ruleLockedMachineIds
      : Array.isArray(values.machine_ids) && values.machine_ids.length
        ? values.machine_ids.map(Number)
        : values.machine_ids_csv
          ? String(values.machine_ids_csv).split(',').map(Number).filter(Boolean)
          : (values.machine_id ? [Number(values.machine_id)] : []);
    if (catId && grpId) {
      const cat = machineTypeWithGroups.find((c) => String(c.machine_category_id) === String(catId));
      const grp = cat?.machine_groups?.find((g) => String(g.machine_group_id) === String(grpId));
      const groupMachineIds = (grp?.machines || []).map((m) => Number(m.machine_id));
      const leaf = buildCascaderLeafValue(selectedMachineIds, groupMachineIds);
      setCascaderValue([catId, grpId, leaf]);
    } else if (catId) {
      setCascaderValue([catId]);
    } else {
      setCascaderValue(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.machine_type_id,
    values.machine_group_id,
    values.machine_id,
    JSON.stringify(values.machine_ids),
    values.machine_ids_csv,
    JSON.stringify(ruleLockedMachineIds),
    machineTypeWithGroups.length,
  ]);

  // Parameters loaded for the selected machine type
  const [machineParameters, setMachineParameters] = useState([]);
  const [machineParamsLoading, setMachineParamsLoading] = useState(false);

  const loadParametersForMachineGroup = (machine_group_id) => {
    if (!machine_group_id) {
      setMachineParameters([]);
      return;
    }
    setMachineParamsLoading(true);
    get_common_sub_categories_by_machine_group(
      (res) => {
        setMachineParamsLoading(false);
        if (res?.data?.data) setMachineParameters(res.data.data);
        else setMachineParameters([]);
      },
      { machine_group_id }
    );
  };

  useEffect(() => {
    // Load parameters when the group is known — either from prop values or from rule-locked setup
    const groupId = values.machine_group_id || ruleLockedGroup;
    if (groupId) loadParametersForMachineGroup(groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.machine_group_id, ruleLockedGroup]);

  const initialValues = {
    ...values,
    machine_type_id: ruleLockedCategory || values.machine_type_id || null,
    machine_type_name: ruleLockedCategory ? ruleLockedLabel : values.machine_type_name || null,
    machine_group_id: ruleLockedGroup || values.machine_group_id || null,
    machine_id: ruleLockedMachineId || values.machine_id || null,
    machine_name: values.machine_name || null,
    machine_ids: ruleLockedMachineIds.length
      ? ruleLockedMachineIds
      : Array.isArray(values.machine_ids) && values.machine_ids.length
        ? values.machine_ids
        : values.machine_ids_csv
          ? String(values.machine_ids_csv).split(',').map(Number).filter(Boolean)
          : (values.machine_id ? [values.machine_id] : []),
    machine_ids_csv: ruleLockedMachineIds.length
      ? ruleLockedMachineIds.join(',')
      : values.machine_ids_csv || (
        Array.isArray(values.machine_ids) && values.machine_ids.length
          ? values.machine_ids.join(',')
          : (values.machine_id ? String(values.machine_id) : '')
      ),
    parameter: values.parameter || null,
    // the config API returns the monitoring unit as `unit`
    parameter_unit: values.parameter_unit || values.unit || null,
    _data_type_list: values.parameter_data_type ? [values.parameter_data_type] : [],
    // Derive frequency from reading_count when API doesn't return monitoring_frequency
    monitoring_frequency: values.monitoring_frequency ??
      (values.reading_count && operation_duration > 0
        ? Math.round(operation_duration / values.reading_count)
        : null),
    is_qr_scan: values.is_qr_scan ?? false,
    is_kitting: values.is_kitting ?? false,
    mode: lockedRuleFields?.mode || values.mode || 'Manual',
    grace_window:
      lockedRuleFields?.grace_window ||
      values.grace_window ||
      formatGraceWindow(parsedGraceWindow.value, parsedGraceWindow.unit),
    grace_window_value: parsedGraceWindow.value,
    grace_window_unit: parsedGraceWindow.unit,
    trigger_type: lockedRuleFields?.trigger_type || values.trigger_type || 'Time Based',
    escalation_delay:
      lockedRuleFields?.escalation_delay || values.escalation_delay || '2 Min',
    // Deviation rule: prefill the rule default; editable unless the rule locks it.
    deviation_rule:
      lockedRuleFields?.deviation_rule || values.deviation_rule || 'none',
    instruction: lockedRuleFields?.instruction ?? values.instruction ?? null,
  };

  const ALLOWED_RULES = ['Photo Capture Required'];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(vals, { resetForm }) => {
        // Resolve Runtime Execution Rules (separate JSON): rule default per key,
        // overridden by any per-item choice (vals.execution_rules).
        const _rDefaults = {
          allow_dynamic_grouping: true,
          auto_include_in_execution_round: true,
          critical_parameter: true,
          allow_partial_submission: false,
        };
        const _rBool = (key, fallback) =>
          lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
            ? !!lockedRuleFields[key]
            : fallback;
        const resolvedExecutionRules = Object.keys(_rDefaults).reduce((acc, key) => {
          const chosen = (vals.execution_rules || {})[key];
          acc[key] = chosen !== undefined && chosen !== null ? !!chosen : _rBool(key, _rDefaults[key]);
          return acc;
        }, {});
        // eslint-disable-next-line no-console
        console.log('[Monitoring submit] vals', vals);
        const graceValue = vals.grace_window_value;
        const machineIds = Array.isArray(vals.machine_ids) && vals.machine_ids.length
          ? vals.machine_ids
          : (vals.machine_id ? [vals.machine_id] : []);
        onSubmit({
          ...vals,
          grace_window_value: graceValue,
          grace_window: formatGraceWindow(graceValue, vals.grace_window_unit),
          frequency: vals.monitoring_frequency ?? 0,
          // Always send the selected deviation rule; default to 'none'.
          deviation_rule: hasNoLimits(vals) ? 'none' : vals.deviation_rule || 'none',
          execution_rules: resolvedExecutionRules,
          machine_ids: machineIds,
          machine_ids_csv: machineIds.join(','),
        });
        resetForm();
      }}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit }) => {
        const filteredSettings = appSettings?.filter(
          (s) => s?.data_type === 'boolean' && ALLOWED_RULES.includes(s?.module)
        );

        // When editing an existing item, parameter_data_type may not come from the API.
        // Derive it from machineParameters so the Lower/Upper Limit row is visible.
        const effectiveDataType = values.parameter_data_type || (() => {
          if (!values.parameter_id || !machineParameters?.length) return null;
          const matched = machineParameters.find(
            (p) => String(p.id) === String(values.parameter_id)
          );
          if (!matched) return null;
          const dt = matched.data_type;
          return Array.isArray(dt) ? dt[0] : dt || null;
        })();

        const clearParam = () => {
          setFieldValue('parameter_id', null);
          setFieldValue('parameter', null);
          setFieldValue('parameter_unit', null);
          setFieldValue('parameter_data_type', null);
          setFieldValue('min_value', null);
          setFieldValue('max_value', null);
          setFieldValue('exp_value', null);
        };

        // ── Rule-driven values ──
        const ruleVal = (key, fallback) =>
          lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
            ? lockedRuleFields[key]
            : fallback;
        const verification = ruleVal('verification', 'Supervisor Required');
        const executionModeLabel = ruleVal('execution_mode_label', 'Dynamic Rounds');
        const runtimeRules = [
          { key: 'allow_dynamic_grouping', label: 'Allow Dynamic Grouping' },
          { key: 'auto_include_in_execution_round', label: 'Auto Include In Execution Round' },
          { key: 'critical_parameter', label: 'Critical Parameter' },
          { key: 'allow_partial_submission', label: 'Allow Partial Submission' },
        ];
        const runtimeDefaults = {
          allow_dynamic_grouping: true,
          auto_include_in_execution_round: true,
          critical_parameter: true,
          allow_partial_submission: false,
        };
        const sectionHeader = (text) => (
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{text}</p>
        );
        const readOnlyBox = (label, val) => (
          <div className="rounded-lg border" style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '10px 14px' }}>
            <p className="text-xs font-medium text-gray-500 m-0 mb-1">{label}</p>
            <p className="text-sm font-semibold text-gray-800 m-0">{val}</p>
          </div>
        );

        return (
          <Form layout="vertical" onFinish={handleSubmit}>
            <UnitSync
              values={values}
              setFieldValue={setFieldValue}
              machineParameters={machineParameters}
            />
            {/* ── Header ── */}
            <div className="mb-2 flex items-start justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-bold text-gray-800 m-0">
                  {values.parameter || 'Monitoring Parameter'}
                </h2>
                {values.machine_type_name && (
                  <p className="text-sm text-blue-600 font-medium m-0">
                    Machine: {values.machine_type_name}
                  </p>
                )}
              </div>
              {/* STATIC visual chips — not persisted */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {values.mode === 'Manual' && (
                  <Tag color="blue" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                    MANUAL
                  </Tag>
                )}
                <Tag color="red" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                  CRITICAL
                </Tag>
                <Tag color="green" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                  GROUP ELIGIBLE
                </Tag>
              </div>
            </div>

            {/* Two-column layout: form on the left (scrollable, pinned footer),
                rule info panel on the right */}
            <div className="flex gap-4 items-start">
              <div className={`min-w-0 ${rulesEnabled ? 'w-[calc(70%-8px)]' : 'w-full'}`}>
                {/* Scrollable form area — submit button stays pinned below it */}
                <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 6 }}>

            {one_machine_rule_enabled && (machine_setup_entries || []).length === 0 && (
              <div
                style={{
                  padding: 10,
                  marginBottom: 12,
                  background: '#fef2f2',
                  border: '1px dashed #fca5a5',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#991b1b',
                }}
              >
                Please setup machines first in the Machine Setup tab. Machine selection is locked until then.
              </div>
            )}
            {one_machine_rule_enabled && (machine_setup_entries || []).length > 0 && (
              <div
                style={{
                  padding: 8,
                  marginBottom: 12,
                  background: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#1e40af',
                }}
              >
                Machine type auto-selected from Machine Setup rule (One Machine Per Operation enabled).
              </div>
            )}

            {/* ── Section: PARAMETER DEFINITION ── */}
            <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Parameter Definition')}
              {/* Machine + Parameter + Unit selection (existing wired fields) */}
              <Row gutter={16} className="mb-2">
                <Col span={8}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Machine
                        {!!one_machine_rule_enabled &&
                          (machine_setup_entries || []).length > 0 && (
                            <Tooltip title="Locked — auto-selected from Machine Setup (One Machine Per Operation rule is enabled in operation type config)">
                              <Lock className="w-3 h-3 text-yellow-600 cursor-help" />
                            </Tooltip>
                          )}
                      </span>
                    }
                    validateStatus={
                      (touched.machine_type_id && errors.machine_type_id) ||
                      (touched.machine_group_id && errors.machine_group_id) ? 'error' : ''
                    }
                    help={
                      (touched.machine_type_id && errors.machine_type_id) ||
                      (touched.machine_group_id && errors.machine_group_id)
                    }
                    style={{ marginBottom: 0 }}
                  >
                    {one_machine_rule_enabled && (machine_setup_entries || []).length > 0 ? (
                      <LockedMachineSetupPanel
                        machines={setupMachineDetails.machines}
                        groupLabel={setupMachineDetails.groupLabel}
                      />
                    ) : (
                    <Cascader
                      value={cascaderValue || undefined}
                      placeholder="Select Category → Group → Machine"
                      showSearch
                      allowClear={!one_machine_rule_enabled}
                      disabled={!!one_machine_rule_enabled}
                      style={{ width: '100%' }}
                      popupClassName="cascader-wide-dropdown"
                      dropdownMenuColumnStyle={{ minWidth: 220 }}
                      expandTrigger="hover"
                      displayRender={(labels) => labels.join(' / ')}
                      onChange={(val, selectedOptions) => {
                        if (!val || val.length === 0) {
                          setCascaderValue(null);
                          setFieldValue('machine_type_id', null);
                          setFieldValue('machine_group_id', null);
                          setFieldValue('machine_type_name', null);
                          setFieldValue('machine_id', null);
                          setFieldValue('machine_name', null);
                          setFieldValue('machine_ids', []);
                          setFieldValue('machine_ids_csv', '');
                          clearParam();
                          loadParametersForMachineGroup(null);
                          return;
                        }
                        const leaf = selectedOptions?.[selectedOptions.length - 1];
                        const catId = leaf?._catId ?? val[0];
                        const grpId = leaf?._grpId ?? val[1];
                        const machineId = leaf?._machineId ?? null;
                        const machineName = leaf?._machineName ?? null;
                        const allIds = leaf?._allIds || (machineId ? [machineId] : []);
                        const label = `${leaf?._catLabel ?? ''} / ${leaf?._grpLabel ?? ''}`;
                        setCascaderValue(val);
                        setFieldValue('machine_type_id', catId);
                        setFieldValue('machine_group_id', grpId);
                        setFieldValue('machine_type_name', label);
                        setFieldValue('machine_id', machineId);
                        setFieldValue('machine_name', machineName);
                        setFieldValue('machine_ids', allIds);
                        setFieldValue('machine_ids_csv', allIds.join(','));
                        clearParam();
                        loadParametersForMachineGroup(grpId);
                      }}
                      options={machineTypeWithGroups.map((cat) => ({
                        label: cat.machine_category_name,
                        value: cat.machine_category_id,
                        children: (cat.machine_groups || []).map((g) => {
                          const machines = g.machines || [];
                          return {
                            label: g.machine_group_name,
                            value: g.machine_group_id,
                            children: [
                              {
                                label: `All Machines (${machines.length})`,
                                value: '__all__',
                                _catId: cat.machine_category_id,
                                _catLabel: cat.machine_category_name,
                                _grpId: g.machine_group_id,
                                _grpLabel: g.machine_group_name,
                                _machineId: machines[0]?.machine_id ?? null,
                                _machineName: machines[0]?.machine_name ?? null,
                                _allIds: machines.map((m) => m.machine_id),
                              },
                              ...machines.map((m) => ({
                                label: m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name,
                                value: m.machine_id,
                                _catId: cat.machine_category_id,
                                _catLabel: cat.machine_category_name,
                                _grpId: g.machine_group_id,
                                _grpLabel: g.machine_group_name,
                                _machineId: m.machine_id,
                                _machineName: m.machine_name,
                                _allIds: [m.machine_id],
                              })),
                            ],
                          };
                        }),
                      }))}
                    />
                    )}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Parameter"
                    validateStatus={touched.parameter_id && errors.parameter_id ? 'error' : ''}
                    help={touched.parameter_id && errors.parameter_id}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={values.parameter_id}
                      placeholder={
                        values.machine_type_id
                          ? 'Select Parameter'
                          : 'Select Machine first'
                      }
                      disabled={!values.machine_type_id}
                      loading={machineParamsLoading}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={(val, option) => {
                        setFieldValue('parameter_id', val || null);
                        if (val) {
                          setFieldValue('parameter', option.label);
                          setFieldValue('parameter_unit', option.parameter_unit);
                          setFieldValue('min_value', null);
                          setFieldValue('max_value', null);
                          setFieldValue('exp_value', null);
                          // auto-select data_type if only one, else clear for user to pick
                          const dtList = Array.isArray(option.data_type)
                            ? option.data_type
                            : option.data_type
                            ? [option.data_type]
                            : [];
                          setFieldValue('_data_type_list', dtList);
                          setFieldValue(
                            'parameter_data_type',
                            dtList.length === 1 ? dtList[0] : null
                          );
                        } else {
                          setFieldValue('parameter', null);
                          setFieldValue('parameter_unit', null);
                          setFieldValue('parameter_data_type', null);
                          setFieldValue('_data_type_list', []);
                        }
                      }}
                      options={machineParameters?.map((p) => ({
                        label: p.parameter_name,
                        value: p.id,
                        parameter_unit: p.unit,
                        data_type: p.data_type,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Unit" style={{ marginBottom: 0 }}>
                    <Input
                      placeholder="Unit"
                      value={values.parameter_unit || ''}
                      disabled
                    />
                  </Form.Item>
                </Col>
                {/* Data Type selector — shown when parameter has multiple data types */}
                {values.parameter_id && values._data_type_list?.length > 1 && (
                  <Col span={8}>
                    <Form.Item label="Data Type" style={{ marginBottom: 0 }}>
                      <Select
                        allowClear
                        placeholder="Select Data Type"
                        value={values.parameter_data_type}
                        onChange={(val) => {
                          setFieldValue('parameter_data_type', val || null);
                          setFieldValue('min_value', null);
                          setFieldValue('max_value', null);
                          setFieldValue('exp_value', null);
                        }}
                        options={values._data_type_list.map((dt) => ({ label: dt, value: dt }))}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {/* Expected Value (Boolean/String) or Min/Max range + Deviation Rule */}
              {values.parameter_id && effectiveDataType && (
                effectiveDataType === 'Boolean' ||
                effectiveDataType === 'String' ? (
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Expected Value"
                        validateStatus={touched.exp_value && errors.exp_value ? 'error' : ''}
                        help={touched.exp_value && errors.exp_value}
                        style={{ marginBottom: 0 }}
                      >
                        {effectiveDataType === 'Boolean' ? (
                          <Select
                            className="w-full"
                            placeholder="Select Expected Value"
                            value={values.exp_value}
                            onChange={(val) => setFieldValue('exp_value', val)}
                            options={[
                              { label: 'True', value: 'True' },
                              { label: 'False', value: 'False' },
                            ]}
                          />
                        ) : (
                          <Input
                            placeholder="Enter Expected Value"
                            value={values.exp_value}
                            onChange={(e) => setFieldValue('exp_value', e.target.value)}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                ) : (
                  <>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item label="Lower Limit" style={{ marginBottom: 0 }}>
                        <InputNumber
                          className="w-full"
                          placeholder="Min"
                          value={values.min_value}
                          onChange={(val) => setFieldValue('min_value', val)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Upper Limit"
                        validateStatus={touched.max_value && errors.max_value ? 'error' : ''}
                        help={touched.max_value && errors.max_value}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          className="w-full"
                          placeholder="Max"
                          value={values.max_value}
                          onChange={(val) => setFieldValue('max_value', val)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span className="flex items-center gap-1">
                            Deviation Rule
                            {isRuleFieldLocked('deviation_rule') && (
                              <Lock className="w-3 h-3 text-yellow-600" />
                            )}
                          </span>
                        }
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          className="w-full"
                          disabled={isRuleFieldLocked('deviation_rule') || hasNoLimits(values)}
                          value={hasNoLimits(values) ? 'none' : values.deviation_rule || 'none'}
                          onChange={(val) => setFieldValue('deviation_rule', val)}
                          options={DEVIATION_RULE_OPTIONS}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {hasNoLimits(values) && (
                      <div
                        className="mt-2 rounded-md text-xs"
                        style={
                          (values.execution_rules?.critical_parameter ?? runtimeDefaults.critical_parameter)
                            ? { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '8px 10px' }
                            : { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '8px 10px' }
                        }
                      >
                        {(values.execution_rules?.critical_parameter ?? runtimeDefaults.critical_parameter)
                          ? 'Note: this is a critical parameter but no lower or upper limit is set — readings will be recorded without any range check, so no out-of-range deviation can be raised.'
                          : 'Note: no lower or upper limit is set — readings will only be recorded (no range check or deviation), so Deviation Rule is fixed to None.'}
                      </div>
                    )}
                  </>
                )
              )}
            </div>

            {/* ── Section: MONITORING SCHEDULE ── */}
            <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Monitoring Schedule')}
              <Row gutter={16} className="mb-2">
                {/* Frequency = monitoring_frequency + frequencyUnit (existing) */}
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Frequency
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="Enter frequency"
                      min={1}
                      value={
                        frequencyUnit === 'min' && values.monitoring_frequency
                          ? +(values.monitoring_frequency / 60).toFixed(2)
                          : values.monitoring_frequency
                      }
                      onChange={(val) => {
                        if (!val) {
                          setFieldValue('monitoring_frequency', null);
                          setFieldValue('reading_count', null);
                          return;
                        }
                        const freqInSec = frequencyUnit === 'min' ? Math.round(val * 60) : val;
                        setFieldValue('monitoring_frequency', freqInSec);
                        if (hasKnownDuration && freqInSec > 0 && freqInSec <= operation_duration) {
                          setFieldValue(
                            'reading_count',
                            Math.floor(operation_duration / freqInSec)
                          );
                        } else if (freqInSec > 0) {
                          setFieldValue('reading_count', null);
                        }
                      }}
                      addonAfter={
                        <Select
                          value={frequencyUnit}
                          onChange={(u) => setFrequencyUnit(u)}
                          style={{ width: 70 }}
                          options={[
                            { value: 'sec', label: 'sec' },
                            { value: 'min', label: 'min' },
                          ]}
                        />
                      }
                    />
                  </Form.Item>
                  {values.reading_count > 0 && operation_duration > 0 && (
                    <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
                      {operation_duration}s ÷ {values.reading_count} ={' '}
                      {Math.round(operation_duration / values.reading_count)}s/reading
                    </p>
                  )}
                  {values.monitoring_frequency > 0 && !values.reading_count && (
                    <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
                      No. of readings:{' '}
                      {!hasKnownDuration
                        ? 'Until completed. Readings continue until the operation is completed.'
                        : 'Cannot derive count — frequency exceeds operation duration.'}
                    </p>
                  )}
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Grace Window
                        {isRuleFieldLocked('grace_window') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    validateStatus={
                      (touched.grace_window_value && errors.grace_window_value) ||
                      (touched.grace_window_unit && errors.grace_window_unit)
                        ? 'error'
                        : ''
                    }
                    help={
                      (touched.grace_window_value && errors.grace_window_value) ||
                      (touched.grace_window_unit && errors.grace_window_unit)
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <div className="flex gap-2">
                      <InputNumber
                        className="flex-1"
                        min={0}
                        step="any"
                        disabled={isRuleFieldLocked('grace_window')}
                        value={values.grace_window_value}
                        onChange={(val) => setFieldValue('grace_window_value', val)}
                        placeholder="Amount"
                      />
                      <Select
                        style={{ width: 130 }}
                        disabled={isRuleFieldLocked('grace_window')}
                        value={values.grace_window_unit}
                        onChange={(val) => setFieldValue('grace_window_unit', val)}
                        options={GRACE_WINDOW_UNITS}
                      />
                    </div>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Trigger Type
                        {isRuleFieldLocked('trigger_type') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      disabled={isRuleFieldLocked('trigger_type')}
                      value={values.trigger_type}
                      onChange={(val) => setFieldValue('trigger_type', val)}
                      options={[
                        { value: 'Time Based', label: 'Time Based' },
                        { value: 'Event Based', label: 'Event Based' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Escalation Delay
                        {isRuleFieldLocked('escalation_delay') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      disabled={isRuleFieldLocked('escalation_delay')}
                      value={values.escalation_delay}
                      onChange={(e) => setFieldValue('escalation_delay', e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* No. of Readings + Interval Based (existing wired fields) */}
              <Row gutter={16} className="mt-2">
                <Col span={12}>
                  <Form.Item
                    label="No. of Readings"
                    validateStatus={
                      touched.reading_count && errors.reading_count ? 'error' : ''
                    }
                    help={touched.reading_count && errors.reading_count}
                    style={{ marginBottom: 0 }}
                  >
                    {!hasKnownDuration && values.monitoring_frequency > 0 ? (
                      <Input disabled value="Until completed" />
                    ) : (
                      <InputNumber
                        className="w-full"
                        placeholder="Readings"
                        value={values.reading_count}
                        min={1}
                        onChange={(val) => {
                          if (!isRuleFieldLocked('mode')) {
                            setFieldValue('mode', 'Manual');
                          }
                          setFieldValue('reading_count', val || null);
                          if (val && hasKnownDuration) {
                            setFieldValue(
                              'monitoring_frequency',
                              Math.round(operation_duration / val)
                            );
                          } else {
                            setFieldValue('monitoring_frequency', null);
                          }
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              {/* Is Start / Is End — optional manual readings at operation
                  start and/or end. Default false; the user sets them. */}
              <Row gutter={16} className="mt-2">
                <Col span={12}>
                  <Form.Item label="Is Start" style={{ marginBottom: 0 }}>
                    <Switch
                      checked={!!values.startTime}
                      onChange={(val) => setFieldValue('startTime', val)}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                    <span className="ml-2 text-xs" style={{ color: '#6b7280' }}>
                      Take a reading at operation start
                    </span>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Is End" style={{ marginBottom: 0 }}>
                    <Switch
                      checked={!!values.endTime}
                      onChange={(val) => setFieldValue('endTime', val)}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                    <span className="ml-2 text-xs" style={{ color: '#6b7280' }}>
                      Take a reading at operation end
                    </span>
                  </Form.Item>
                </Col>
              </Row>

              {/* No duration, no frequency, no reading count ⇒ readings are taken
                  manually, any number of times, until the operation ends. */}
              {!hasKnownDuration &&
                !values.monitoring_frequency &&
                !values.reading_count && (
                  <div
                    className="mt-2 rounded-md text-xs"
                    style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      color: '#1e40af',
                      padding: '8px 10px',
                    }}
                  >
                    No duration, frequency, or reading count set — the operator can take
                    multiple readings at any time until the operation ends.
                  </div>
                )}
            </div>

            {/* ── Section: EXECUTION BEHAVIOR — hidden when Rules app-setting is off ── */}
            {rulesEnabled && <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Execution Behavior')}

              {/* Execution Mode (existing wired field) */}
              <Row gutter={16} className="mb-3">
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Execution Mode{' '}
                        {isRuleFieldLocked('mode') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    validateStatus={touched.mode && errors.mode ? 'error' : ''}
                    help={touched.mode && errors.mode}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={values.mode}
                      placeholder="Mode"
                      disabled={isRuleFieldLocked('mode')}
                      onChange={(val) => setFieldValue('mode', val)}
                      options={[
                        { value: 'Manual', label: 'Manual' },
                        { value: 'Semi-Automatic', label: 'Semi-Auto' },
                        { value: 'Automatic', label: 'Automatic' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {/* Runtime Execution Rules + Execution Mode/Verification moved to the
                  right-side rule info panel. */}
            </div>}

            {/* ── Section: Instruction ── */}
            <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Instruction')}
              <Form.Item
                label={
                  <span className="flex items-center gap-1">
                    Instruction{' '}
                    {lockedRuleFields?.instruction && (
                      <Lock className="w-3 h-3 text-yellow-600" />
                    )}
                  </span>
                }
                style={{ marginBottom: 0 }}
              >
                <TextArea
                  rows={2}
                  placeholder="Instruction"
                  value={values.instruction}
                  disabled={!!lockedRuleFields?.instruction}
                  onChange={(e) => setFieldValue('instruction', e.target.value)}
                />
              </Form.Item>
            </div>

            {/* ── Rules Panel — hidden when Rules app-setting is off ── */}
            {rulesEnabled && filteredSettings?.length > 0 && (
              <div
                className="rounded-lg border w-full mb-4"
                style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '12px 16px' }}
              >
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Rules
                </p>
                <div className="flex flex-col gap-1">
                  {filteredSettings?.map((setting, idx) => (
                    <div key={setting.id ?? idx} className="flex items-center justify-between py-1">
                      <span className="text-sm font-medium" style={{ color: '#9ca3af' }}>
                        {setting.module}
                      </span>
                      <Tag
                        color={setting.status ? 'green' : 'default'}
                        style={{ margin: 0, minWidth: 36, textAlign: 'center' }}
                      >
                        {setting.status ? 'Yes' : 'No'}
                      </Tag>
                    </div>
                  ))}
                </div>
              </div>
            )}
                </div>
              </div>

              {/* ── RIGHT: rule info panel — only when rulesEnabled ── */}
              {rulesEnabled && (
                <div className="w-[30%] min-w-0 flex-shrink-0">
                  <div
                    className="rounded-lg border bg-white mb-4"
                    style={{ borderColor: '#e5e7eb', padding: '14px 16px' }}
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 m-0">
                      Runtime Execution Rules
                    </p>
                    <div className="flex flex-col gap-2">
                      {runtimeRules.map(({ key, label }) => {
                        const er = values.execution_rules || {};
                        const checked = er[key] !== undefined && er[key] !== null
                          ? !!er[key]
                          : !!ruleVal(key, runtimeDefaults[key]);
                        const locked = isRuntimeRuleLocked(key);
                        return (
                          <Checkbox
                            key={key}
                            checked={checked}
                            disabled={locked}
                            onChange={(e) =>
                              setFieldValue('execution_rules', {
                                ...(values.execution_rules || {}),
                                [key]: e.target.checked,
                              })
                            }
                          >
                            <span className="inline-flex items-center gap-1">
                              {label}
                              {locked && <Lock className="w-3 h-3 text-yellow-600" />}
                            </span>
                          </Checkbox>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex flex-col gap-2">
                      {readOnlyBox('Execution Mode', executionModeLabel)}
                      {readOnlyBox('Verification', verification)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Full-width footer — Save spans the whole card */}
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10 }}>
              {Object.keys(errors || {}).length > 0 && (
                <div
                  style={{
                    marginBottom: 8,
                    padding: 8,
                    background: '#fef2f2',
                    border: '1px dashed #fca5a5',
                    borderRadius: 6,
                    fontSize: 11,
                    color: '#991b1b',
                  }}
                >
                  Fix these fields: {Object.keys(errors).join(', ')}
                </div>
              )}
              <Button
                type="primary"
                htmlType="submit"
                block
                disabled={!!one_machine_rule_enabled && (machine_setup_entries || []).length === 0}
              >
                {editIndex !== null ? 'Update' : 'Save Configuration'}
              </Button>
              {!!one_machine_rule_enabled && (machine_setup_entries || []).length === 0 && (
                <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6, textAlign: 'center' }}>
                  Please setup machines first in the Machine Setup tab before adding monitoring.
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export const MonitoringCard = ({ fromView, item, onEdit, onDelete, operation_duration = 0 }) => {
  const parameter_unit = item.parameter_unit || item.unit;
  const {
    machine_name,
    machine_type_name,
    parameter,
    parameter_id,
    parameter_data_type,
    sensor,
    sensor_id,
    exp_value,
    reading_count,
    monitoring_frequency,
    min_value,
    max_value,
    instruction,
    mode,
    grace_window,
    grace_window_value,
    grace_window_unit,
    startTime,
    endTime,
    machine_register,
    machine_registers,
    _from_rule,
  } = item;

  const { isDark } = useTheme();

  // Use machine_registers if available, otherwise fall back to machine_register (backward compatibility)
  const registerData = machine_registers || machine_register;

  // Calculate reading interval time in seconds
  const readingIntervalTime =
    reading_count && operation_duration ? Math.round(operation_duration / reading_count) : 0;

  const showLabel = (label, id) => label || `${id ? 'ID: ' + id : 'N/A'}`;
  const graceWindow = parseGraceWindow(
    grace_window,
    grace_window_value,
    grace_window_unit
  );

  return (
    <div
      className={`rounded-lg border shadow-sm hover:shadow-md transition p-3 text-sm ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-100 rounded-md">
            <Activity className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p
              className="font-semibold text-gray-900 leading-tight truncate text-base w-64"
              title={showLabel(parameter, parameter_id)}
            >
              {showLabel(parameter, parameter_id)}
            </p>
            {(machine_type_name || machine_name) && (
              <p className="text-xs text-blue-600 font-medium">
                Machine: {machine_type_name || machine_name}
              </p>
            )}
            <p className="text-xs text-gray-600">
              {parameter_unit ? `Unit: ${parameter_unit}` : 'No unit'}
              {parameter_data_type ? ` · ${parameter_data_type}` : ''}
            </p>
          </div>
        </div>

        {_from_rule && (
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: '#fef9c3', color: '#92400e' }}
            title="Locked — assigned from Operation Type rule"
          >
            <Lock className="w-3 h-3" />
            Rule
          </div>
        )}

        {!fromView && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                onClick={() => onEdit()}
                title="Edit monitoring parameter"
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                onClick={() => onDelete()}
                title="Delete monitoring parameter"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mode + tags row */}
      <div className="flex items-center gap-2 my-2 flex-wrap">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-3.5 h-3.5 text-green-600" />
          <span className="text-xs text-gray-700 font-medium">
            Mode: {mode || 'Manual'}
          </span>
        </span>
        {monitoring_frequency && (
          <Tag color="green" className="text-xs">
            Interval Based
          </Tag>
        )}
        {monitoring_frequency && (
          <Tag color="purple" className="text-xs">
            <Clock className="w-3 h-3 inline mr-0.5" />
            {formatFrequency(monitoring_frequency)}
          </Tag>
        )}
        {!monitoring_frequency && !reading_count && (
          <Tag color="blue" className="text-xs">
            Continuous
          </Tag>
        )}
        <Tag color="gold" className="text-xs">
          Grace: {formatGraceWindow(graceWindow.value, graceWindow.unit)}
        </Tag>
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        {/* Show Expected Value if exists, otherwise show the limits */}
        {exp_value ? (
          <div className="bg-purple-50 rounded-md p-2.5">
            <p className="text-xs text-purple-700 font-medium mb-1">Expected Value</p>
            <p className="text-purple-900 font-semibold">{exp_value}</p>
          </div>
        ) : (
          <div className="bg-indigo-50 rounded-md p-2.5">
            <p className="text-xs text-indigo-700 font-medium mb-1">Limits</p>
            <p className="text-indigo-900 font-semibold">
              {min_value ?? '–'} to {max_value ?? '–'}{' '}
              <span className="text-sm font-normal text-indigo-700">
                {parameter_unit || 'Unit'}
              </span>
            </p>
          </div>
        )}

        {reading_count && (
          <div className="bg-purple-50 rounded-md p-2.5">
            <p className="text-xs text-purple-700 font-medium mb-1">Reading Count</p>
            <p className="text-purple-900 font-semibold flex items-center gap-1">
              <Clock className="w-3 h-3" /> {reading_count}
            </p>
            {readingIntervalTime > 0 && (
              <p className="text-xs text-purple-600 mt-1">Interval: {readingIntervalTime} sec</p>
            )}
          </div>
        )}

        {(mode === 'Automatic' || mode === 'Semi-Automatic') && sensor_id && (
          <div className="bg-green-50 rounded-md p-2.5">
            <p className="text-xs text-green-700 font-medium mb-1">Sensor</p>
            <p
              className="text-green-900 font-semibold truncate flex items-center gap-1"
              title={showLabel(sensor, sensor_id)}
            >
              <Gauge className="w-3 h-3" />
              {showLabel(sensor, sensor_id)}
            </p>
          </div>
        )}

        {(Number(min_value) >= 0 || Number(max_value) >= 0) && (
          <div className="bg-orange-50 rounded-md p-2.5">
            <p className="text-xs text-orange-700 font-medium mb-1">Range</p>
            <p className="text-orange-900 font-semibold">
              {min_value !== null ? min_value : 'N/A'} - {max_value !== null ? max_value : 'N/A'}{' '}
              <span className="text-sm font-normal text-orange-700">{parameter_unit || ''}</span>
            </p>
          </div>
        )}

        {monitoring_frequency && (
          <div className="bg-gray-50 rounded-md p-2.5 col-span-2">
            <p className="text-xs text-gray-600 font-medium mb-1">Interval Settings</p>
            <div className="flex gap-2">
              {startTime && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                  <Clock className="w-3 h-3 mr-1" />
                  Start: {startTime}
                </span>
              )}
              {endTime && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-700">
                  <Clock className="w-3 h-3 mr-1" />
                  End: {endTime}
                </span>
              )}
              {!startTime && !endTime && (
                <span className="text-xs text-gray-500">No time constraints</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Instruction */}
      {instruction && (
        <div className={`mt-3 rounded-md p-2.5 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Instruction
          </p>
          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {instruction}
          </p>
        </div>
      )}

      {/* Machine & Registers */}
      {registerData && registerData.length > 0 && (
        <div
          className="mt-3 rounded-md p-2.5"
          style={{ backgroundColor: isDark ? '#1e293b' : '#f0f9ff' }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: isDark ? '#93c5fd' : '#0369a1' }}>
            Machines & Registers
          </p>
          <div className="overflow-x-auto pb-1">
            <div className="flex gap-3" style={{ minWidth: 'fit-content' }}>
              {registerData.map((machineReg, index) => (
                <div key={index} className="flex-shrink-0" style={{ minWidth: '200px' }}>
                  <Tag color="blue" className="mb-1">
                    {machineReg.machine_name || `Machine ${machineReg.machine_id}`}
                  </Tag>
                  {((machineReg.register_ids && machineReg.register_ids.length > 0) ||
                    (machineReg.registers && machineReg.registers.length > 0)) && (
                    <div className="flex flex-wrap gap-1">
                      {(machineReg.registers || machineReg.register_ids)?.map((reg, idx) => {
                        let displayName;
                        let key;

                        // If reg is an object with id and name
                        if (typeof reg === 'object' && reg !== null) {
                          key = reg.id || reg.register_id || idx;
                          displayName = reg.name || reg.register_name || `#${key}`;
                        } else {
                          // If reg is just an ID, check for corresponding name in register_names
                          key = reg;
                          const nameFromArray = machineReg.register_names?.[idx];
                          displayName = nameFromArray || `#${reg}`;
                        }

                        return (
                          <Tag key={key} color="cyan" className="text-xs">
                            {displayName}
                          </Tag>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
